import { useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { StoreDispatch } from "../../store/configure-store"
import { Backlog } from "../../cmps/util-cmps/backlog"
import { Spinner } from "../../cmps/util-cmps/spinner"
import { UserMsg } from "../../models/user"
import { setUserMsg } from "../../store/user"
import { httpService } from "../../services/http-service"
import { useEscapeKey } from "../../hooks/use-escape-key"
import { useScrollLock } from "../../hooks/use-scroll-lock"
import { useFocusTrap } from "../../hooks/use-focus-trap"
import { EntityHealth } from "../../pages/database/entity-page"

interface EntityCompleteSetupModalProps {
    companyName: string
    entity: any
    health: EntityHealth
    onSuccess: () => void
    onClose: () => void
}

export const EntityCompleteSetupModal: React.FC<EntityCompleteSetupModalProps> = ({
    companyName, entity, health, onSuccess, onClose
}) => {
    const dispatch = useDispatch<StoreDispatch>()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [domainInput, setDomainInput] = useState('')
    const [createContext, setCreateContext] = useState(!health.hasContext)
    const [createAuditYear, setCreateAuditYear] = useState(!health.hasCurrentYearAudit)
    const [auditors, setAuditors] = useState<{ id: number; name: string }[]>([])
    const [selectedAuditorId, setSelectedAuditorId] = useState<number | null>(null)

    useEffect(() => {
        const fetchAuditors = async () => {
            try {
                const res = await httpService.graphql('auditech', `{ auditors { id name } }`)
                setAuditors(res.auditors || [])
            } catch { /* ignore */ }
        }
        if (!health.hasCurrentYearAudit) fetchAuditors()
    }, [health.hasCurrentYearAudit])

    useEscapeKey(onClose)
    useScrollLock()
    const trapRef = useFocusTrap<HTMLDivElement>()

    const currentYear = new Date().getFullYear()

    const canSubmit = () => {
        if (createContext && !domainInput.trim()) return false
        if (createAuditYear && !selectedAuditorId) return false
        return createContext || createAuditYear
    }

    const onSubmit = async () => {
        if (!canSubmit() || isSubmitting) return
        setIsSubmitting(true)
        try {
            const results: string[] = []

            if (createContext && domainInput.trim()) {
                const data = await httpService.post('onboarding/entity-context-domains', {
                    company_name: companyName,
                    domains: domainInput.trim(),
                })
                results.push(`Entity context: ${(data.entities || []).length} entities updated`)
            }

            if (createAuditYear && selectedAuditorId) {
                const data = await httpService.post('onboarding/create-audit-year', {
                    company_name: companyName,
                    auditor_id: selectedAuditorId,
                })
                results.push(`Audit year ${data.year || currentYear}: ${(data.entities || []).length} entities updated`)
            }

            const msg: UserMsg = { type: 'success', txt: results.join(' | ') || 'Setup completed' }
            dispatch(setUserMsg({ msg }))
            onSuccess()
        } catch (err) {
            console.error('Complete setup error:', err)
            const msg: UserMsg = { type: 'danger', txt: 'Failed to complete setup' }
            dispatch(setUserMsg({ msg }))
        } finally {
            setIsSubmitting(false)
        }
    }

    return <div ref={trapRef}>
        <section className="company-form-modal" role="dialog" aria-modal="true" aria-label="Complete Setup">
            <header>
                <h2>Complete Setup — {entity.name}</h2>
                <button className="close-btn" onClick={onClose} aria-label="Close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>

            <section className="modal-form-body">
                <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '1rem' }}>
                    The following items are missing for <strong>{entity.name}</strong>. Select which to create and click "Create".
                </p>

                {!health.hasContext && (
                    <div className="form-section-card">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                            <input type="checkbox" checked={createContext} onChange={e => setCreateContext(e.target.checked)} />
                            Create Entity Context
                        </label>
                        {createContext && (
                            <div style={{ marginTop: '0.75rem' }}>
                                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                                    Company Domains
                                </label>
                                <input
                                    type="text"
                                    value={domainInput}
                                    onChange={e => setDomainInput(e.target.value)}
                                    placeholder="e.g. acme.com, acme.io"
                                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.82rem' }}
                                />
                                <p style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                                    This will create entity context records for all entities of this company.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {!health.hasCurrentYearAudit && (
                    <div className="form-section-card" style={{ marginTop: '0.75rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                            <input type="checkbox" checked={createAuditYear} onChange={e => setCreateAuditYear(e.target.checked)} />
                            Create Audit Year {currentYear}
                        </label>
                        <p style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                            This will create audit records for {currentYear} for all entities that don't have one yet.
                        </p>
                        {createAuditYear && (
                            <div style={{ marginTop: '0.5rem' }}>
                                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.25rem' }}>Auditor</label>
                                <select
                                    value={selectedAuditorId ?? ''}
                                    onChange={e => setSelectedAuditorId(e.target.value ? Number(e.target.value) : null)}
                                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.82rem', fontFamily: 'inherit', backgroundColor: 'white' }}
                                >
                                    <option value="">Select Auditor</option>
                                    {auditors.map(a => (
                                        <option key={a.id} value={a.id}>{a.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                )}

                {!health.hasSystems && (
                    <div className="form-section-card" style={{ marginTop: '0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, fontSize: '0.85rem', color: '#9ca3af' }}>
                            <span style={{ fontSize: '1rem' }}>&#9432;</span>
                            Entity Systems Not Configured
                        </div>
                        <p style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                            Add systems via the Entity Systems section in the database view.
                        </p>
                    </div>
                )}

                <div className="form-actions" style={{ marginTop: '1rem' }}>
                    {!isSubmitting && <button className="main" onClick={onSubmit} disabled={!canSubmit() || isSubmitting}>Create</button>}
                    {isSubmitting && <span className="spinner"><Spinner isSmall={true} /></span>}
                </div>
            </section>
        </section>
        <Backlog zIndex={199} onCloseBacklog={onClose} />
    </div>
}
